import React, { useEffect, useRef, useState } from 'react';

import { AppStateStatus, View } from 'react-native';

import { useDispatch, useSelector } from 'react-redux';

import { isPast, millisecondsToSeconds } from 'date-fns';
import { Subscription, timer } from 'rxjs';

import { LANGUAGE_KEYS } from 'config/languageKeys.ts';

import { getPinsStatus } from 'store/user/actions';
import { selectAccessPinLockedTime } from 'store/user/selectors';

import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import { convertHMS } from 'utils/hms';

import Text, { ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    appState: AppStateStatus;
};

function PinTimer({ appState }: Props) {
    const accessPinLockedTime = useSelector(selectAccessPinLockedTime);

    const [currentTime, setCurrentTime] = useState(
        (accessPinLockedTime != null &&
            millisecondsToSeconds(accessPinLockedTime - Date.now())) ||
            0,
    );
    const dispatch = useDispatch();
    const prevBlockedTime = useRef<number | undefined>(undefined);

    const prevAppState = useRef(appState);

    useEffect(() => {
        if (
            appState === 'active' &&
            prevAppState.current !== 'active' &&
            accessPinLockedTime
        ) {
            const timeLeft = millisecondsToSeconds(
                accessPinLockedTime - Date.now(),
            );

            setCurrentTime(timeLeft);
        }

        prevAppState.current = appState;
    }, [appState, accessPinLockedTime]);

    const handleGetPinsStatusWithRetry = () => {
        const retryGetPinsStatusAction = () =>
            new Promise<void>((resolve, reject) => {
                dispatch(
                    getPinsStatus({
                        onSuccess: () => {
                            resolve();
                        },
                        onFailure: (error) => {
                            if (isErrorNeedsRetry(error)) {
                                reject(error);
                            } else {
                                resolve();
                            }
                        },
                    }),
                );
            });

        dispatch(
            getPinsStatus({
                onSuccess() {
                    // No handler is needed.
                },
                onFailure: (error) => {
                    if (isErrorNeedsRetry(error)) {
                        RetryController.showVitalDialog({
                            actionButtonTitle: LANGUAGE_KEYS.CLOSE_BUTTON,
                            actionButtonOnPress: () => {
                                RetryController.hideVitalDialog();
                            },
                        });
                        RetryController.registerRetryCallback(
                            retryGetPinsStatusAction,
                            {
                                retryDuration: 10 * 60 * 1000,
                                maxRetryCount: 10,
                            },
                        );
                        RetryController.executeRetry();
                    }
                },
            }),
        );
    };

    useEffect(() => {
        let subscription: Subscription | undefined;

        if (
            accessPinLockedTime &&
            !isPast(accessPinLockedTime) &&
            !prevBlockedTime.current
        ) {
            prevBlockedTime.current = accessPinLockedTime;
            const time = millisecondsToSeconds(
                accessPinLockedTime - Date.now(),
            );

            const source$ = timer(time, 1000);

            subscription = source$.subscribe({
                next: () => {
                    const timeLeft = millisecondsToSeconds(
                        accessPinLockedTime - Date.now(),
                    );

                    if (timeLeft <= 1) {
                        handleGetPinsStatusWithRetry();
                    }

                    setCurrentTime(timeLeft);
                },
            });
        }

        if (accessPinLockedTime && isPast(accessPinLockedTime)) {
            handleGetPinsStatusWithRetry();
        }

        return () => {
            if (subscription) {
                subscription.unsubscribe();
            }
        };
    }, [accessPinLockedTime, dispatch]);

    return (
        <View style={styles.container}>
            <Text color={ETextColor.neutral} style={styles.time}>
                {convertHMS(currentTime, true)}
            </Text>
        </View>
    );
}

export default PinTimer;
